import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TranslocoService} from '@ngneat/transloco';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {HelpArticleResult, HelpRelatedLink} from '@shared/models/contextual-help.model';
import {ContextualHelpService} from '@shared/services/contextual-help/contextual-help.service';
import {HelpMarkdownService} from '@shared/services/contextual-help/help-markdown.service';

export type ContextHelpVariant = 'icon' | 'inline' | 'link' | 'topbar';

@Component({
  selector: 'app-context-help',
  templateUrl: './context-help.component.html',
  styleUrls: ['./context-help.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContextHelpComponent implements OnInit, OnDestroy {
  /**
   * Stable, language-neutral help identifier, e.g. `payroll.employee.taxProvince`.
   * Callers never pass a language: the article is resolved against the current UI locale.
   */
  @Input() helpKey: string;

  @Input() variant: ContextHelpVariant = 'icon';

  /** Optional catalogue metadata. Unused at runtime; carried for help-catalogue scanning. */
  @Input() module: string;
  @Input() feature: string;
  @Input() view: string;
  @Input() field: string;

  panelOpen = false;
  result: HelpArticleResult | null = null;
  renderedContent: SafeHtml | null = null;
  activeIdentifier: string | null = null;

  private lastFocus: HTMLElement | null = null;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private helpService: ContextualHelpService,
    private markdown: HelpMarkdownService,
    private sanitizer: DomSanitizer,
    private transloco: TranslocoService,
    private host: ElementRef<HTMLElement>,
    private changeDetector: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    // Contextual help follows the application language: switching language re-resolves the same
    // identifier without any page reload.
    this.transloco.langChanges$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.panelOpen && this.activeIdentifier) {
          this.loadArticle(this.activeIdentifier);
        } else {
          this.result = null;
          this.renderedContent = null;
          this.changeDetector.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get ariaTopic(): string {
    return this.field || this.helpKey || '';
  }

  openHelp(): void {
    const identifier = (this.helpKey || '').trim();
    if (!identifier) {
      return;
    }

    this.lastFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    this.activeIdentifier = identifier;
    this.panelOpen = true;
    this.loadArticle(identifier);
  }

  closeHelp(): void {
    if (!this.panelOpen) {
      return;
    }

    this.panelOpen = false;
    this.changeDetector.markForCheck();

    const target = this.lastFocus;
    if (target) {
      setTimeout(() => target.focus());
    }
  }

  onRelatedLink(link: HelpRelatedLink): void {
    if (link.type === 'external' && link.url) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Internal links are language-neutral identifiers, re-resolved in the current locale.
    if (link.type === 'help' && link.helpIdentifier) {
      this.activeIdentifier = link.helpIdentifier;
      this.loadArticle(link.helpIdentifier);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeHelp();
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: Event): void {
    if (!this.panelOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    if (this.host.nativeElement.contains(target)) {
      return;
    }

    this.closeHelp();
  }

  /** Localized message for a non-success lookup. */
  message(): string {
    const key = this.result && this.result.messageKey
      ? this.result.messageKey
      : 'contextualHelp.unavailable';
    return this.transloco.translate(key);
  }

  private loadArticle(identifier: string): void {
    this.result = {state: 'loading'};
    this.renderedContent = null;
    this.changeDetector.markForCheck();

    this.helpService.getArticle(identifier)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        value => {
          this.result = value;
          this.renderedContent = this.renderArticleContent(value);
          this.changeDetector.markForCheck();
        },
        () => {
          this.result = {state: 'error', messageKey: 'contextualHelp.error'};
          this.renderedContent = null;
          this.changeDetector.markForCheck();
        });
  }

  private renderArticleContent(value: HelpArticleResult): SafeHtml | null {
    const article = value && value.article;
    if (!article || !article.content) {
      return null;
    }

    if (article.contentFormat !== 'markdown') {
      // Unknown formats are shown as plain text rather than guessed at.
      return null;
    }

    // The HTML is produced by HelpMarkdownService, which escapes all authored input first.
    return this.sanitizer.bypassSecurityTrustHtml(this.markdown.render(article.content));
  }
}
